import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { X, Pencil, Trash2, Check, MapPin, Loader2, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Manage Venues modal — inline rename (cascades to matches) and delete
 * (blocked for venues still assigned to any match). Opened from the
 * schedule header. Refreshes the parent event once anything changes so all
 * per-row venue dropdowns pick up the new list.
 */
export function ManageVenuesModal({ open, onClose, event, onChanged }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [newVenueName, setNewVenueName] = useState('');

  // Per-venue usage counts so we can grey out the trash icon when a venue
  // is still assigned to any match and explain WHY it can't be removed.
  const usage = useMemo(() => {
    const map = {};
    (event?.schedule || []).forEach(m => {
      const v = (m.venue || '').trim().toLowerCase();
      if (!v) return;
      map[v] = (map[v] || 0) + 1;
    });
    return map;
  }, [event?.schedule]);

  if (!open) return null;
  const venues = event?.venues || [];

  const authFetch = (path, opts = {}) => fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('session_token')}`,
    },
  });

  const startRename = (venue) => {
    setRenamingId(venue);
    setRenameValue(venue);
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue('');
  };
  const commitRename = async (oldName) => {
    const next = renameValue.trim();
    if (!next) { toast.error('Name cannot be empty'); return; }
    if (next.toLowerCase() === oldName.toLowerCase() && next === oldName) {
      cancelRename();
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(`/api/events/${event.id}/venues/rename`, {
        method: 'PATCH',
        body: JSON.stringify({ old: oldName, new: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to rename venue');
      const cascaded = data.matches_updated || 0;
      toast.success(
        cascaded
          ? `Renamed. Updated ${cascaded} match${cascaded !== 1 ? 'es' : ''}.`
          : 'Venue renamed'
      );
      cancelRename();
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'Failed to rename venue');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (venue) => {
    if (!window.confirm(`Delete venue "${venue}"?`)) return;
    setBusy(true);
    try {
      const res = await authFetch(
        `/api/events/${event.id}/venues/${encodeURIComponent(venue)}`,
        { method: 'DELETE' },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to delete venue');
      toast.success(`Venue "${venue}" deleted`);
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'Failed to delete venue');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    const name = newVenueName.trim();
    if (!name) { toast.error('Enter a venue name'); return; }
    const existing = event?.venues || [];
    if (existing.some(v => v.toLowerCase() === name.toLowerCase())) {
      toast.info('That venue already exists');
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(`/api/events/${event.id}/venues`, {
        method: 'PATCH',
        body: JSON.stringify({ venues: [...existing, name] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add venue');
      toast.success(`Venue "${name}" added`);
      setNewVenueName('');
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'Failed to add venue');
    } finally {
      setBusy(false);
    }
  };

  const handleAssignAll = async (venue) => {
    // Default to overwriting every match. Mixed-site leagues can opt out
    // with a Shift+click as a power-user shortcut (preserves per-match
    // venues that are already set). Locked matches are always skipped.
    const overwrite = !(window.event && window.event.shiftKey);
    const scheduleLen = (event?.schedule || []).length;
    const alreadyAssigned = (event?.schedule || []).filter(m => (m.venue || '').trim()).length;
    const confirmMsg = overwrite
      ? `Set "${venue}" as the venue for all ${scheduleLen} matches? This will overwrite any venues already assigned.`
      : `Set "${venue}" only on the ${scheduleLen - alreadyAssigned} unassigned matches?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const res = await authFetch(
        `/api/events/${event.id}/schedule/bulk-venue`,
        {
          method: 'POST',
          body: JSON.stringify({ venue, overwrite, skip_locked: true }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to assign venue');
      toast.success(
        data.changed === 0
          ? 'No matches needed updating'
          : `"${venue}" set on ${data.changed} match${data.changed === 1 ? '' : 'es'}`
      );
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'Failed to assign venue');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
      data-testid="manage-venues-modal"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 text-[#00247D]">
            <MapPin className="w-5 h-5" />
            <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
              MANAGE VENUES
            </h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="manage-venues-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-4 pt-4 pb-2 border-b border-slate-100 bg-slate-50">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Add a venue
          </label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input
              value={newVenueName}
              onChange={(e) => setNewVenueName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              placeholder="e.g. ABC Stadium, Court 2 — JGHS"
              className="h-9 text-sm"
              maxLength={80}
              disabled={busy}
              data-testid="venue-add-input"
            />
            <Button
              onClick={handleAdd}
              disabled={busy || !newVenueName.trim()}
              className="h-9 bg-[#00247D] hover:bg-[#001A5C] text-white px-4"
              data-testid="venue-add-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Once added, pick the venue for each match from the Venue column on the schedule table.
          </p>
        </div>

        <div className="p-4 max-h-[380px] overflow-y-auto">
          {venues.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              No venues yet. Add your first one above and it will appear in the Venue column for every match.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="venue-list">
              {venues.map(v => {
                const count = usage[v.toLowerCase()] || 0;
                const editing = renamingId === v;
                return (
                  <li key={v} className="py-2 flex items-center gap-2" data-testid={`venue-row-${v}`}>
                    {editing ? (
                      <>
                        <Input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') commitRename(v); if (e.key === 'Escape') cancelRename(); }}
                          className="h-8 text-sm"
                          maxLength={80}
                          autoFocus
                          data-testid={`venue-rename-input-${v}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={() => commitRename(v)}
                          disabled={busy}
                          data-testid={`venue-rename-save-${v}`}
                        >
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-slate-600"
                          onClick={cancelRename}
                          disabled={busy}
                          data-testid={`venue-rename-cancel-${v}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{v}</div>
                          <div className="text-[11px] text-slate-500">
                            {count === 0
                              ? 'Not assigned to any match'
                              : `Assigned to ${count} match${count !== 1 ? 'es' : ''}`}
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => handleAssignAll(v)}
                          disabled={busy || (event?.schedule || []).length === 0}
                          title={'Set "' + v + '" as the venue for every match (Shift+click = only unassigned)'}
                          data-testid={`venue-assign-all-${v}`}
                        >
                          <Zap className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-500 hover:text-[#00247D] hover:bg-blue-50"
                          onClick={() => startRename(v)}
                          disabled={busy}
                          title="Rename venue"
                          data-testid={`venue-rename-${v}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className={`h-8 w-8 ${count === 0 ? 'text-rose-500 hover:text-rose-700 hover:bg-rose-50' : 'text-slate-300 cursor-not-allowed'}`}
                          onClick={() => count === 0 && handleDelete(v)}
                          disabled={busy || count > 0}
                          title={count === 0 ? 'Delete venue' : `Reassign the ${count} match${count !== 1 ? 'es' : ''} using this venue before deleting`}
                          data-testid={`venue-delete-${v}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500">
          Renaming a venue updates every match already assigned to it. Delete
          is disabled for venues still in use.
        </div>
      </div>
    </div>
  );
}

export default ManageVenuesModal;
